import React, {useEffect, useRef, useState, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import QRCode from "qrcode.react";
import "./style.scss";
import {API, processApiResponse} from "../../services/HttpHandler";
import TabsMenu from "../../components/TabsMenu";
import Loader from "../../components/Loader";
import "../../components/Loader/style.scss";
import {prepareQRDisplayData} from "./qrUtils";
import BlockBtn from "../../components/BlockBtn";
import {
  setCustomerSlotDetails,
  getCustomerSlotDetails,
} from "../../services/SessionStorage";
import SlotBookingError from "./SlotBookingError";
import ConfirmedBookingOffice from "./BookingWithQRCode/ConfirmedBookingOffice";
import BookingDetailList from "../../components/BookingDetailList";
import {QRCODE_ACTIVE_STATUS, BOOKING_ERRORS} from "../../constants";
import {CarouselContainer, CarouselChild} from "../../components/Carousel";
import {
  checkContentAvlInArray,
  // dataDashboard
} from "../../util";
import ConfirmBox from "../../components/ConfirmBox";

import config from "../../../config";

const SlotBooked = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorInfo, setErrorInfo] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const {t} = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bookingTypeMapping = {
    1: "mallOnly",
    2: "storeOnly",
    3: "mallStore",
  };
  const updatedCustomerSlotDetails = useRef(getCustomerSlotDetails());
  const slotBookingPayload = useRef({});
  const displayData = useRef([]);

  const processsMultipleQRData = (data) => {
    const processedDisplayData = data.map((qrData) => {
      qrData.country = updatedCustomerSlotDetails.current.country;
      return prepareQRDisplayData(qrData);
    });
    return processedDisplayData;
  };

  const showLimitedQRs = () => {
    const tempdata = updatedCustomerSlotDetails.current.fetchOnlyQR;
    displayData.current = processsMultipleQRData(tempdata);
    updatedCustomerSlotDetails.current.qrDisplayData = displayData.current;
    setCustomerSlotDetails(updatedCustomerSlotDetails.current);
    setLoading(false);
  };

  const createBooking = useCallback(async () => {
    let bookingAlreadyExistsError = false;
    let limitExeeded = false;
    let errorArray = [];
    let successArray = [];
    try {
      const {
        userId,
        emailId,
        userTel,
        userName,
        userIdType,
        acceptTermsConditions,
        acceptUseOfMyInformation,
        acceptReadTermsConditions,
        currentBookingDetails: {
          selectedSlot: {slotId, noOfPeople, serviceTypes},
        },
        customerType,
        source,
        customerEmail,
        customerFirstName,
        customerLastName,
        customerPhoneNumber,
        customerLoyaltyPoints,
      } = updatedCustomerSlotDetails.current;
      const stores = updatedCustomerSlotDetails.current.selectedMallStores;
      const bookingType =
        bookingTypeMapping[updatedCustomerSlotDetails.current.selectedType];
      slotBookingPayload.current = {
        userId,
        slotId,
        noOfPeople,
        serviceTypes,
        customerType,
        source,
        userIdType,
        acceptTermsConditions,
        acceptUseOfMyInformation,
        acceptReadTermsConditions,
        stores,
        bookingType,
        ...(emailId && {emailId}),
        ...(userTel && {userTel}),
        ...(userName && {userName}),
        customerEmail,
        customerFirstName,
        customerLastName,
        customerPhoneNumber,
        customerLoyaltyPoints: Number(customerLoyaltyPoints),
      };

      let response = await API.post(
        config.endPoints.bookings,
        slotBookingPayload.current
      );

      response = await processApiResponse(response);
      updatedCustomerSlotDetails.current.fetchOnlyQR = [];
      for (let i = 0; i < response.length; i++) {
        if (!response[i].error) {
          response[i].status = QRCODE_ACTIVE_STATUS;
          response[i].country = updatedCustomerSlotDetails.current.country;
          const storeBUName =
            response[i].businessUnit + "-" + response[i].storeName;
          successArray.push(storeBUName);
          updatedCustomerSlotDetails.current.fetchOnlyQR.unshift(response[i]);
        } else {
          if (response[i].error === BOOKING_ERRORS.USER_ALREADY_HAS_BOOKING) {
            bookingAlreadyExistsError = true;
            const errorText =
              t("notification.error.USER_ALREADY_HAS_BOOKING") +
              response[i].businessUnit +
              "-" +
              response[i].storeName;
            errorArray.push(errorText);
          }
          if (response[i].error === BOOKING_ERRORS.BOOKING_LIMIT_EXCEEDED) {
            limitExeeded = true;
            const errorText =
              t("notification.error.BOOKING_LIMIT_EXCEEDED") +
              response[i].businessUnit +
              "-" +
              response[i].storeName;
            errorArray.push(errorText);
          }
          if (response[i].error === BOOKING_ERRORS.NO_SLOT_AVAILABLE) {
            const errorText =
              t("notification.error.NO_SLOT_AVAILABLE") +
              response[i].businessUnit +
              "-" +
              response[i].storeName;
            errorArray.push(errorText);
          }
          if (response[i].error === BOOKING_ERRORS.NO_CURRENT_SLOT_AVAILABLE) {
            const errorText =
              t("notification.error.NO_SLOT_AVAILABLE") +
              response[i].businessUnit +
              "-" +
              response[i].storeName +
              t("notification.error.NO_CURRENT_SLOT_AVAILABLE") +
              response[i].nextAvailableSlot;
            errorArray.push(errorText);
          }
          if (response[i].error === BOOKING_ERRORS.CUSTOMER_ALREADY_IN_MALL) {
          }
          if (response[i].error === BOOKING_ERRORS.SERVICE_TYPE_NOT_AVAILABLE) {
            const errorText =
              t("notification.error.SERVICE_TYPE_NOT_AVAILABLE") +
              response[i].businessUnit +
              "-" +
              response[i].storeName;
            errorArray.push(errorText);
          }
        }
      }
      if (errorArray.length > 0) {
        if (errorArray.length === response.length) {
          setOpenConfirm(false);
        } else {
          const successText =
            t("notification.text.bookingSuccessful") +
            successArray.join(" and ");
          errorArray.unshift(successText);
          setOpenConfirm(true);
        }
      } else {
        setOpenConfirm(false);
      }
      setErrorInfo(errorArray);
      const dateOnly =
        updatedCustomerSlotDetails.current.fetchOnlyQR[0]?.startTime.slice(
          0,
          10
        );
      const existingBookings =
        updatedCustomerSlotDetails.current.existingBookingDetails;
      let newObj = {};
      if (existingBookings && existingBookings.length > 0) {
        let [sameDateBookings] = existingBookings.filter((e) => {
          return Object.keys(e)[0] === dateOnly;
        });
        if (sameDateBookings) {
          let sameDayDetails = sameDateBookings[dateOnly];
          for (
            let i = 0;
            i < updatedCustomerSlotDetails.current.fetchOnlyQR.length;
            i++
          ) {
            sameDayDetails.push(
              updatedCustomerSlotDetails.current.fetchOnlyQR[i]
            );
          }
        } else {
          newObj[dateOnly] = updatedCustomerSlotDetails.current.fetchOnlyQR;
          updatedCustomerSlotDetails.current.existingBookingDetails.push(
            newObj
          );
        }
      } else {
        newObj[dateOnly] = updatedCustomerSlotDetails.current.fetchOnlyQR;
        updatedCustomerSlotDetails.current.existingBookingDetails = [newObj];
      }
      showLimitedQRs();
    } catch (e) {
      if (bookingAlreadyExistsError) {
        setError(t("error.bookingExists"));
      } else if (limitExeeded) {
        setError(t("notification.error.BOOKING_LIMIT_EXCEEDED"));
      } else {
        setError(t("error.slotUnavailable"));
      }
      setOpenConfirm(false);
    } finally {
      /**
       * The currentBookingDetails in session storage was deleted to ensure latest details
       * The store details was required in Mallplaza. Hence BU check in if condition
       * And only selected slot is deleted and store information is retained
       */
      if (
        checkContentAvlInArray(
          config.stand_alone_bu_list,
          updatedCustomerSlotDetails.current.buList
        )
      ) {
        delete updatedCustomerSlotDetails.current.currentBookingDetails
          .selectedSlot;
      } else {
        delete updatedCustomerSlotDetails.current.currentBookingDetails;
      }
      setCustomerSlotDetails(updatedCustomerSlotDetails.current);
      setLoading(false);
    }
  }, [bookingTypeMapping, showLimitedQRs, t]);

  const fetchExistingBooking = useCallback(
    async (existingBookingData) => {
      try {
        const request = existingBookingData.map(async (bookingData, index) => {
          const qrDataUrl = config.endPoints.qrdata;
          const bookingIdPlaceHolder = /bookingIdPlaceholder/gi;
          const url = qrDataUrl.replace(
            bookingIdPlaceHolder,
            bookingData.bookingId
          );
          let response = await API.get(url);
          const data = await processApiResponse(response);
          existingBookingData[index].status = data.status;
          return data;
        });
        const response = await Promise.all(request);
        //displayData.current = processsMultipleQRData(response);
        displayData.current = processsMultipleQRData(response);
        updatedCustomerSlotDetails.current.qrDisplayData = displayData.current;
        setCustomerSlotDetails(updatedCustomerSlotDetails.current);
      } catch (error) {
        setError(t("error.fetchBookingFailed"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (
      updatedCustomerSlotDetails.current.currentBookingDetails &&
      !updatedCustomerSlotDetails.current.showlimited
    ) {
      createBooking();
    } else if (updatedCustomerSlotDetails.current.showlimited) {
      showLimitedQRs();
    } else {
      fetchExistingBooking(
        updatedCustomerSlotDetails.current.existingBookingDetails
      );
    }
  }, []);

  // useEffect(() => {
  //   // for data dashboard
  //   const URL = window.location.href.split("/")[2];
  //   const officeApp = updatedCustomerSlotDetails.current.isOffice
  //     ? URL + "/office"
  //     : URL + "/app";
  //   const datamap = new Map();
  //   datamap.set("pageName", ADOBEDTM_PAGE_NAME.CT_Confirmation);
  //   datamap.set("prop1", URL);
  //   datamap.set("prop2", officeApp);
  //   dataDashboard(datamap);
  // }, []);

  const handleClick = () => {
    // const datamap = new Map();
    // if (updatedCustomerSlotDetails.current.isOffice) {
    //   datamap.set("events", "event55=1");
    // } else {
    //   datamap.set("events", "event18=1");
    // }
    // dataDashboard(datamap);
    navigate("/customer/displayAppointments");
  };

  const isBanco = () => {
    if (updatedCustomerSlotDetails.current.bu === config.showNoonSlotsBU) {
      return true;
    } else {
      return false;
    }
  };
  const MultiQrCarousel = () => {
    return (
      <CarouselContainer>
        {displayData.current.map((data, index) => (
          <CarouselChild key={index}>
            <div className="slide each-booking-styling">
              {updatedCustomerSlotDetails.current.isOffice ? (
                <ConfirmedBookingOffice data={data} />
              ) : (
                <>
                  <BookingDetailList
                    endTime={isBanco() ? "" : data.endTime}
                    isOffice={updatedCustomerSlotDetails.current.isOffice}
                    region={data.location}
                    serviceType={data.serviceType.toString()}
                    startTime={data.startTime}
                    storeName={data.storeName}
                    timeZone={data.timeZone}
                    country={data.country}
                    bu={data.businessUnit}
                  />
                  <div className="just-line">
                    <hr />
                  </div>
                  <div className="flex">
                    <QRCode
                      id="slotbook-qrcode"
                      value={data.apiResponse}
                      renderAs={"png"}
                      className="qr-code"
                      size={256}
                    />
                  </div>
                  <p className="disclaimer-text">
                    Necesitarás el siguiente código para ingresar a la tienda
                  </p>
                </>
              )}
            </div>
          </CarouselChild>
        ))}
      </CarouselContainer>
    );
  };
  return loading ? (
    <div className="loader-container">
      <Loader />
    </div>
  ) : (
    <>
      <TabsMenu path="slotBooked" />
      <div className="slot-booked-main-container">
        <label className="centre-heading">{t("msteams.slot.label")}</label>

        {openConfirm && (
          <ConfirmBox
            title={"Atención"}
            text={errorInfo}
            close={() => setOpenConfirm(false)}
          />
        )}
        {!error ? (
          <>
            {isBanco() ? (
              <div className="qr-banco-text">
                {t("banco.display.slot.text")}
              </div>
            ) : (
              ""
            )}
            <MultiQrCarousel />
          </>
        ) : (
          <SlotBookingError error={error} />
        )}
        <div className="footer-div">
          <BlockBtn name={t("moveto.myappnt")} handleClick={handleClick} />
        </div>
      </div>
    </>
  );
};

export default SlotBooked;
